import { apiCall } from "./fetchClient";
import {
  GET_BLOG_LIST_URL,
  ADD_BLOG_URL,
  GET_BLOG_DETAILS_URL,
  UPDATE_BLOG_URL,
  DELETE_BLOG_URL,
  TOGGLE_BLOG_STATUS_URL,
} from "./url";

/**
 * Common API Service for Blog Management
 * Follows the Super Admin backend contract specifications.
 */

// 1. Get Blog List with pagination, search, status and category filtering
export const getBlogListApi = async ({
  page = 1,
  limit = 10,
  search = "",
  status = "All",
  category = "",
} = {}) => {
  const params = new URLSearchParams();
  if (page) params.append("page", page.toString());
  if (limit) params.append("limit", limit.toString());
  if (search && search.trim()) params.append("search", search.trim());
  if (status && status !== "All") params.append("status", status.trim());
  if (category && category !== "All" && category !== "All Categories") {
    params.append("category", category.trim());
  }

  const queryString = params.toString();
  const url = queryString ? `${GET_BLOG_LIST_URL}?${queryString}` : GET_BLOG_LIST_URL;
  return await apiCall(url, "GET", null, false, false);
};

// 2. Get Single Blog Details
export const getBlogDetailsApi = async (blogId) => {
  if (!blogId) throw new Error("Blog ID is required");
  return await apiCall(`${GET_BLOG_DETAILS_URL}${blogId}`, "GET", null, false, false);
};

// Helper to determine if data contains a binary File / Blob
const hasBinaryFile = (data) => {
  const file = data?.coverImage || data?.image;
  return file instanceof File || file instanceof Blob;
};

// Helper to construct payload (FormData or JSON)
const buildBlogPayload = (blogData) => {
  const isFile = hasBinaryFile(blogData);
  const isEdit = Boolean(blogData._id || blogData.id);

  if (isFile) {
    const formData = new FormData();
    if (blogData.title && blogData.title.trim()) formData.append("title", blogData.title.trim());
    if (blogData.slug && blogData.slug.trim()) formData.append("slug", blogData.slug.trim().toLowerCase());
    if (blogData.content) formData.append("content", blogData.content);
    
    if (blogData.category && blogData.category.trim()) {
      formData.append("category", blogData.category.trim());
    } else if (isEdit && blogData.category !== undefined) {
      formData.append("category", "");
    }
    
    if (blogData.keywords) {
      const keywordsStr = Array.isArray(blogData.keywords)
        ? blogData.keywords.join(", ")
        : blogData.keywords.toString().trim();
      if (keywordsStr) formData.append("keywords", keywordsStr);
      else if (isEdit) formData.append("keywords", "");
    } else if (isEdit && blogData.keywords !== undefined) {
      formData.append("keywords", "");
    }

    if (blogData.author) {
      const authorStr = typeof blogData.author === "object"
        ? (blogData.author?.name ? JSON.stringify(blogData.author) : "")
        : blogData.author.toString().trim();
      if (authorStr) formData.append("author", authorStr);
      else if (isEdit) formData.append("author", "");
    } else if (isEdit && blogData.author !== undefined) {
      formData.append("author", "");
    }

    if (blogData.metaDescription && blogData.metaDescription.trim()) {
      formData.append("metaDescription", blogData.metaDescription.trim());
    } else if (isEdit && blogData.metaDescription !== undefined) {
      formData.append("metaDescription", "");
    }
    
    // Status is always sent (has default value)
    if (blogData.status) {
      formData.append("status", blogData.status);
    }
    
    if (blogData.publishedAt) {
      formData.append("publishedAt", new Date(blogData.publishedAt).toISOString());
    } else if (isEdit && blogData.publishedAt !== undefined) {
      formData.append("publishedAt", "");
    }

    const imageFile = blogData.coverImage instanceof File || blogData.coverImage instanceof Blob
      ? blogData.coverImage
      : blogData.image;

    if (imageFile instanceof File || imageFile instanceof Blob) {
      formData.append("coverImage", imageFile);
      formData.append("image", imageFile);
    }

    return { body: formData, isFormData: true };
  }

  // JSON payload
  const jsonBody = {};
  if (blogData.title && blogData.title.trim()) jsonBody.title = blogData.title.trim();
  if (blogData.slug && blogData.slug.trim()) jsonBody.slug = blogData.slug.trim().toLowerCase();
  if (blogData.content) jsonBody.content = blogData.content;
  
  if (blogData.category && blogData.category.trim()) {
    jsonBody.category = blogData.category.trim();
  } else if (isEdit && blogData.category !== undefined) {
    jsonBody.category = "";
  }
  
  if (blogData.keywords) {
    const kw = Array.isArray(blogData.keywords)
      ? blogData.keywords.filter(Boolean)
      : (typeof blogData.keywords === "string" ? blogData.keywords.split(",").map(k => k.trim()).filter(Boolean) : []);
    if (kw.length > 0) jsonBody.keywords = kw;
    else if (isEdit) jsonBody.keywords = [];
  } else if (isEdit && blogData.keywords !== undefined) {
    jsonBody.keywords = [];
  }
  
  if (blogData.author) {
    const authorVal = typeof blogData.author === "object"
      ? (blogData.author?.name ? blogData.author : null)
      : blogData.author.toString().trim();
    if (authorVal) jsonBody.author = authorVal;
    else if (isEdit) jsonBody.author = "";
  } else if (isEdit && blogData.author !== undefined) {
    jsonBody.author = "";
  }
  
  if (blogData.metaDescription && blogData.metaDescription.trim()) {
    jsonBody.metaDescription = blogData.metaDescription.trim();
  } else if (isEdit && blogData.metaDescription !== undefined) {
    jsonBody.metaDescription = "";
  }
  
  // Status is always sent (has default value)
  if (blogData.status) jsonBody.status = blogData.status;
  
  if (blogData.publishedAt) {
    jsonBody.publishedAt = new Date(blogData.publishedAt).toISOString();
  } else if (isEdit && blogData.publishedAt !== undefined) {
    jsonBody.publishedAt = "";
  }
  
  if (typeof blogData.coverImage === "string" && blogData.coverImage.trim()) {
    jsonBody.coverImage = blogData.coverImage.trim();
  } else if (typeof blogData.image === "string" && blogData.image.trim()) {
    jsonBody.image = blogData.image.trim();
  }

  return { body: jsonBody, isFormData: false };
};

// 3. Create Blog
export const createBlogApi = async (blogData) => {
  const { body, isFormData } = buildBlogPayload(blogData);
  return await apiCall(ADD_BLOG_URL, "POST", body, isFormData, true);
};

// 4. Update Blog
export const updateBlogApi = async (blogId, blogData) => {
  if (!blogId) throw new Error("Blog ID is required");
  const { body, isFormData } = buildBlogPayload(blogData);
  return await apiCall(`${UPDATE_BLOG_URL}${blogId}`, "PUT", body, isFormData, true);
};

// 5. Delete Blog
export const deleteBlogApi = async (blogId) => {
  if (!blogId) throw new Error("Blog ID is required");
  return await apiCall(`${DELETE_BLOG_URL}${blogId}`, "DELETE", null, false, true);
};

// 6. Toggle Blog Status
export const toggleBlogStatusApi = async (blogId, status) => {
  if (!blogId) throw new Error("Blog ID is required");
  return await apiCall(
    `${TOGGLE_BLOG_STATUS_URL}${blogId}`,
    "PUT",
    { status },
    false,
    true
  );
};
